import type { TaxCodeResponse } from './types';

import {
  queryOptions,
  skipToken,
  useQuery,
  type QueryFunctionContext,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { ms } from 'ms';

import type { UUID } from '~/types/common';

import type { VestigasHTTPError } from '@vestigas/data';

import { queryKeysTaxCode } from '../queryKeys';

import { fetchTaxCode } from './fetchTaxCode';

/**
 * Builds reusable query options for one tax code.
 *
 * @param taxCodeId - The tax code id, or undefined to skip the query.
 * @param options - Additional query options.
 * @returns Query options for the tax-code detail request.
 */
// fallow-ignore-next-line unused-export -- Query options are the domain's supported prefetch API.
export const getTaxCodeQueryOptions = <TData = TaxCodeResponse>({
  taxCodeId,
  options,
}: {
  taxCodeId: UUID | undefined;
  options?: Omit<
    UseQueryOptions<TaxCodeResponse, VestigasHTTPError, TData>,
    'queryKey' | 'queryFn'
  >;
}) =>
  queryOptions({
    queryFn: taxCodeId
      ? async ({ signal }: QueryFunctionContext) =>
          fetchTaxCode(taxCodeId, { signal })
      : skipToken,
    queryKey: queryKeysTaxCode.get(taxCodeId ?? ''),
    staleTime: ms('60s'),
    throwOnError: false,
    ...options,
  });

/**
 * Fetches one tax code by id with TanStack Query.
 *
 * @param taxCodeId - The tax code id, or undefined to skip the query.
 * @param options - Additional query options.
 * @returns The query result.
 * @see https://app.dev.vestigas.com/redoc#tag/Tax-Codes
 */
export const useQueryTaxCode = <TData = TaxCodeResponse>(
  taxCodeId: UUID | undefined,
  options?: Omit<
    UseQueryOptions<TaxCodeResponse, VestigasHTTPError, TData>,
    'queryKey' | 'queryFn'
  >,
) => useQuery(getTaxCodeQueryOptions<TData>({ taxCodeId, options }));
